import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Sparkle, Cube, Diamond, UploadSimple, PaintBrushBroad, Storefront, Check, ArrowRight } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1624588057318-5f1b2eb81012?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const CRAFT = "https://images.unsplash.com/photo-1624585179018-25699030cb8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState({});
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const steps = [
    { icon: UploadSimple, t: t("landing.step1t"), d: t("landing.step1d") },
    { icon: Cube, t: t("landing.step2t"), d: t("landing.step2d") },
    { icon: PaintBrushBroad, t: t("landing.step3t"), d: t("landing.step3d") },
    { icon: Storefront, t: t("landing.step4t"), d: t("landing.step4d") },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-obsidian selection:bg-gold/30">
      {/* Nav */}
      <nav className="sticky top-0 z-50 glass" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/hephy-logo.png" alt="Hephy 3D" className="w-8 h-8 object-contain" />
            <span className="font-serif text-xl tracking-tight">Hephy 3D</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher dark={false} />
            <a href="#pricing" className="hidden sm:block"><Button variant="ghost" data-testid="nav-pricing-btn" className="rounded-full">{t("landing.pricing")}</Button></a>
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn" className="rounded-full">{t("landing.login")}</Button></Link>
            <Link to="/register"><Button data-testid="nav-register-btn" className="rounded-full bg-obsidian hover:bg-obsidian/90 text-white">{t("landing.startFree")}</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">{t("landing.heroEyebrow")}</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-5">
              {t("landing.heroTitle1")}<span className="italic">{t("landing.heroTitleItalic")}</span>{t("landing.heroTitle2")}
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-lg">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/register"><Button size="lg" data-testid="hero-cta-btn" className="rounded-full bg-gold hover:bg-gold/90 text-obsidian font-semibold px-8 h-12">
                {t("landing.heroCta")} <ArrowRight className="ml-2" /></Button></Link>
              <a href="#pricing"><Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-obsidian/20">{t("landing.viewPlans")}</Button></a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="relative">
            <div className="absolute -inset-6 bg-gold/10 blur-3xl rounded-full" />
            <img src={HERO} alt="Hephy 3D" className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]" />
          </motion.div>
        </div>
      </header>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-serif text-4xl tracking-tight text-center mb-4">{t("landing.howTitle")}</h2>
        <p className="text-center text-neutral-500 mb-14 max-w-xl mx-auto">{t("landing.howSubtitle")}</p>
        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }} className="bg-white border border-neutral-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-lg transition-transform">
              <div className="w-12 h-12 rounded-xl bg-obsidian text-gold flex items-center justify-center mb-5"><s.icon size={24} weight="duotone" /></div>
              <div className="text-xs text-gold font-mono mb-1">0{i + 1}</div>
              <h3 className="font-semibold text-lg mb-2">{s.t}</h3>
              <p className="text-sm text-neutral-500">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Craft banner */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative rounded-3xl overflow-hidden">
          <img src={CRAFT} alt="Hephy 3D" className="w-full h-[380px] object-cover" />
          <div className="absolute inset-0 bg-obsidian/55 flex items-center">
            <div className="px-10 sm:px-16 max-w-xl">
              <h2 className="font-serif text-4xl sm:text-5xl text-white tracking-tight">{t("landing.craftTitle")}</h2>
              <p className="text-white/80 mt-4">{t("landing.craftBody")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">{t("landing.plansEyebrow")}</span>
          <h2 className="font-serif text-4xl tracking-tight mt-3">{t("landing.plansTitle")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {Object.entries(plans).map(([id, p], i) => (
            <div key={id} className={`rounded-2xl p-8 border ${id === "pro" ? "bg-obsidian text-white border-obsidian shadow-2xl scale-[1.03]" : "bg-white border-neutral-200"}`} data-testid={`plan-card-${id}`}>
              {id === "pro" && <div className="inline-block text-xs uppercase tracking-widest text-gold mb-3 font-semibold">{t("landing.mostPopular")}</div>}
              <h3 className="font-serif text-2xl">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="text-5xl font-bold tracking-tight">${p.price}</span>
                <span className={`mb-1 text-sm ${id === "pro" ? "text-white/60" : "text-neutral-400"}`}>{t("landing.perMonth")}</span>
              </div>
              <ul className="mt-7 space-y-3">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm">
                    <Check size={18} className="text-gold shrink-0 mt-0.5" weight="bold" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register"><Button className={`w-full mt-8 rounded-full h-11 ${id === "pro" ? "bg-gold text-obsidian hover:bg-gold/90" : "bg-obsidian text-white hover:bg-obsidian/90"}`} data-testid={`plan-select-${id}`}>{t("landing.choose", { name: p.name })}</Button></Link>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-4 text-sm text-neutral-500">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2"><img src="/hephy-logo.png" alt="Hephy 3D" className="w-5 h-5 object-contain" /> Hephy 3D © 2026</div>
            <div className="flex items-center gap-1"><Sparkle size={14} className="text-gold" /> {t("landing.madeFor")}</div>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 pt-2 border-t border-neutral-100">
            <a href="/pricing" className="hover:text-gold" data-testid="footer-pricing">{t("landing.footerPricing")}</a>
            <a href="/privacy" className="hover:text-gold" data-testid="footer-privacy">{t("landing.footerPrivacy")}</a>
            <a href="/terms" className="hover:text-gold" data-testid="footer-terms">{t("landing.footerTerms")}</a>
            <a href="/data-deletion" className="hover:text-gold" data-testid="footer-data-deletion">{t("landing.footerDataDeletion")}</a>
            <a href="/cookies" className="hover:text-gold" data-testid="footer-cookies">{t("landing.footerCookies")}</a>
            <a href="/contact" className="hover:text-gold" data-testid="footer-contact">{t("landing.footerContact")}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
